import $ from 'jquery';
import { Workbook, parseWorkbookData } from './sheets/engine.ts';
import { SheetGrid } from './sheets/grid.ts';
import { cellName, parseCellName, parseRangeRef } from './sheets/types.ts';
import { parseCsvRows, workbookModelToCsv } from './sheets-csv.js';

const DEFAULT_TITLE = 'Untitled spreadsheet';

let plotlyPromise = null;

function getSecret() {
  try {
    const currentUser =
      window.SimpleAuth && typeof window.SimpleAuth.getCurrentUser === 'function'
        ? window.SimpleAuth.getCurrentUser()
        : null;
    const candidates = [
      window.userData && window.userData.secret,
      currentUser && currentUser.secret,
      window.localStorage.getItem('secret')
    ];
    const secret = candidates.find((value) => {
      if (typeof value !== 'string') return false;
      const normalized = value.trim().toLowerCase();
      return normalized && normalized !== 'null' && normalized !== 'undefined';
    });
    return secret ? secret.trim() : '';
  } catch {
    return '';
  }
}

function setStatus(elements, message, isError = false) {
  elements.status.text(message || '');
  elements.status.toggleClass('text-danger', Boolean(isError));
}

async function apiFetch(path, options = {}) {
  const secret = getSecret();
  // The sheets APIs are private. Anonymous visitors can still use the local
  // workbook, but must not probe protected endpoints and create expected 401s.
  if (!secret) {
    throw new Error('Sign in required');
  }
  const headers = {
    Accept: 'application/json',
    ...(options.headers || {})
  };
  if (secret) headers.Authorization = `Bearer ${secret}`;
  if (options.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';

  const response = await fetch(path, { ...options, headers });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.success === false) {
    throw new Error(payload.error || `Request failed (${response.status})`);
  }
  return payload;
}

function selectionRange(sel) {
  return `${cellName(sel.sr, sel.sc)}:${cellName(sel.er, sel.ec)}`;
}

function activeCellName(grid) {
  return cellName(grid.sel.ar, grid.sel.ac);
}

function sheetIndexFor(wb, activeSheet, value) {
  if (typeof value === 'number' && Number.isInteger(value)) {
    return Math.max(0, Math.min(value, wb.model.sheets.length - 1));
  }
  if (typeof value === 'string' && value.trim()) {
    const byName = wb.sheetIndex(value.trim());
    if (byName >= 0) return byName;
    const numeric = Number(value);
    if (Number.isInteger(numeric)) return Math.max(0, Math.min(numeric, wb.model.sheets.length - 1));
  }
  return activeSheet;
}

function getRangeValues(wb, si, range) {
  const ref = parseRangeRef(range);
  if (!ref) return null;
  const rows = [];
  for (let r = ref.sr; r <= ref.er; r += 1) {
    const row = [];
    for (let c = ref.sc; c <= ref.ec; c += 1) {
      row.push({
        value: wb.getValue(si, r, c),
        display: wb.getDisplay(si, r, c)
      });
    }
    rows.push(row);
  }
  return rows;
}

function asNumber(value) {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function traceForChart(wb, si, chart) {
  const rows = getRangeValues(wb, si, chart.range);
  if (!rows || !rows.length || !rows[0].length) return [];
  const type = chart.type || 'bar';

  if (type === 'pie') {
    const body = rows.length > 1 ? rows.slice(1) : rows;
    return [
      {
        type: 'pie',
        labels: body.map((row, idx) => row[0]?.display || `Row ${idx + 1}`),
        values: body.map((row) => asNumber(row[1]?.value ?? row[0]?.value) ?? 0)
      }
    ];
  }

  const body = rows.length > 1 ? rows.slice(1) : rows;
  const x = body.map((row, idx) => row[0]?.display || String(idx + 1));
  const traces = [];
  const firstDataCol = rows[0].length > 1 ? 1 : 0;
  for (let c = firstDataCol; c < rows[0].length; c += 1) {
    traces.push({
      type: type === 'line' ? 'scatter' : 'bar',
      mode: type === 'line' ? 'lines+markers' : undefined,
      name: rows[0][c]?.display || `Series ${c - firstDataCol + 1}`,
      x,
      y: body.map((row) => asNumber(row[c]?.value) ?? 0)
    });
  }
  return traces;
}

async function getPlotly() {
  if (!plotlyPromise) {
    plotlyPromise = import('plotly.js-basic-dist-min').then((mod) => mod.default || mod);
  }
  return plotlyPromise;
}

async function renderCharts(state, elements) {
  const charts = state.wb.charts(state.activeSheet);
  elements.charts.empty();
  if (!charts.length) {
    elements.charts.append($('<div class="text-muted small">No charts</div>'));
    return;
  }

  const Plotly = await getPlotly();
  charts.forEach((chart) => {
    const holder = $('<div class="sheets-chart"></div>');
    const remove = $('<button type="button" class="btn btn-sm btn-link text-danger">Remove</button>');
    const wrap = $('<div></div>');
    wrap.append(remove, holder);
    elements.charts.append(wrap);
    remove.on('click', () => {
      state.wb.removeChart(state.activeSheet, chart.id);
      renderCharts(state, elements);
    });
    const traces = traceForChart(state.wb, state.activeSheet, chart);
    Plotly.react(holder[0], traces, {
      title: { text: chart.title || chart.range, font: { size: 14 } },
      margin: { l: 36, r: 14, t: 38, b: 34 },
      showlegend: traces.length > 1,
      paper_bgcolor: '#ffffff',
      plot_bgcolor: '#ffffff'
    }, { displayModeBar: false, responsive: true });
  });
}

function renderTabs(state, elements) {
  elements.tabs.empty();
  state.wb.model.sheets.forEach((sheet, idx) => {
    const button = $('<button type="button" class="sheets-tab"></button>');
    button.text(sheet.name || `Sheet${idx + 1}`);
    button.toggleClass('is-active', idx === state.activeSheet);
    button.on('click', () => {
      state.activeSheet = idx;
      state.grid.setSheet(idx);
      state.wb.model.active = idx;
      renderTabs(state, elements);
      renderCharts(state, elements);
      updateFormulaBar(state, elements);
    });
    elements.tabs.append(button);
  });
}

function updateFormulaBar(state, elements) {
  if (!state.grid) return;
  elements.cellRef.text(activeCellName(state.grid));
  if (!state.formulaFocused) {
    elements.formula.val(state.wb.getInput(state.activeSheet, state.grid.sel.ar, state.grid.sel.ac));
  }
}

function commitFormulaBar(state, elements) {
  const input = String(elements.formula.val() || '');
  state.wb.setCellInput(state.activeSheet, state.grid.sel.ar, state.grid.sel.ac, input);
  state.grid.focus();
  updateFormulaBar(state, elements);
  renderCharts(state, elements);
}

function setWorkbook(state, elements, wb) {
  if (state.grid) state.grid.destroy();
  state.wb = wb;
  state.activeSheet = Math.max(0, Math.min(wb.model.active || 0, wb.model.sheets.length - 1));
  const gridEl = elements.grid[0];
  state.grid = new SheetGrid(gridEl, wb, {
    theme: {
      bg: '#ffffff',
      gridLine: '#e2e8f0',
      text: '#172033',
      headerBg: '#f4f7fa',
      headerText: '#475569',
      headerBorder: '#d8e0e8',
      selBg: 'rgba(68,167,120,0.12)',
      selBorder: '#44a778',
      activeBorder: '#177f59',
      errText: '#b42318',
      boolText: '#5c3a9e',
      headerActiveBg: '#dff4ea'
    },
    onSelectionChange: () => updateFormulaBar(state, elements),
    onEditStateChange: (editing, text) => {
      state.editing = editing;
      if (editing) elements.formula.val(text);
      else updateFormulaBar(state, elements);
    },
    onModelChange: () => {
      state.dirty = true;
      setStatus(elements, 'Unsaved');
      renderCharts(state, elements);
    }
  });
  if (state.activeSheet) state.grid.setSheet(state.activeSheet);
  renderTabs(state, elements);
  renderCharts(state, elements);
  updateFormulaBar(state, elements);
  setTimeout(() => state.grid.focus(), 0);
}

async function loadSheets(elements) {
  const payload = await apiFetch('/api/sheets');
  elements.openList.find('option[value!=""]').remove();
  payload.sheets.forEach((sheet) => {
    elements.openList.append($('<option></option>').val(sheet.id).text(sheet.title));
  });
  return payload.sheets;
}

async function openSheet(state, elements, id) {
  if (!id) return;
  setStatus(elements, 'Opening...');
  const payload = await apiFetch(`/api/sheets/${encodeURIComponent(id)}`);
  state.currentId = payload.sheet.id;
  elements.title.val(payload.sheet.title || DEFAULT_TITLE);
  elements.openList.val(state.currentId);
  setWorkbook(state, elements, new Workbook(parseWorkbookData(payload.sheet.data)));
  state.dirty = false;
  setStatus(elements, 'Opened');
}

async function saveSheet(state, elements) {
  const title = String(elements.title.val() || DEFAULT_TITLE).trim() || DEFAULT_TITLE;
  const body = JSON.stringify({
    title,
    data: JSON.stringify(state.wb.model),
    secret: getSecret()
  });
  setStatus(elements, 'Saving...');
  const path = state.currentId ? `/api/sheets/${encodeURIComponent(state.currentId)}` : '/api/sheets';
  const method = state.currentId ? 'PUT' : 'POST';
  const payload = await apiFetch(path, { method, body });
  state.currentId = payload.sheet.id;
  state.dirty = false;
  elements.title.val(payload.sheet.title);
  await loadSheets(elements);
  elements.openList.val(state.currentId);
  setStatus(elements, 'Saved');
}

function newSheet(state, elements) {
  state.currentId = null;
  elements.title.val(DEFAULT_TITLE);
  elements.openList.val('');
  setWorkbook(state, elements, new Workbook());
  state.dirty = false;
  setStatus(elements, 'New');
}

function downloadCsv(state, elements) {
  const csv = workbookModelToCsv(state.wb.model, state.activeSheet);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const title = String(elements.title.val() || 'spreadsheet').trim().replace(/[^a-z0-9_-]+/gi, '-');
  a.href = url;
  a.download = `${title || 'spreadsheet'}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function applyRowsToSheet(state, rows) {
  const used = state.wb.usedRange(state.activeSheet);
  if (used.rows || used.cols) {
    state.wb.clearRange(state.activeSheet, 0, 0, Math.max(used.rows - 1, 0), Math.max(used.cols - 1, 0));
  }
  const updates = [];
  rows.forEach((row, r) => {
    row.forEach((value, c) => {
      updates.push({ r, c, input: String(value ?? '') });
    });
  });
  state.wb.setCells(state.activeSheet, updates);
}

async function importFile(state, elements, file) {
  if (!file) return;
  setStatus(elements, 'Importing...');
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
    try {
      const xlsxPackage = 'xlsx';
      const XLSX = await import(/* @vite-ignore */ xlsxPackage);
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const first = workbook.Sheets[workbook.SheetNames[0]];
      applyRowsToSheet(state, XLSX.utils.sheet_to_json(first, { header: 1, raw: false }));
    } catch (error) {
      setStatus(elements, 'XLSX import unavailable', true);
      return;
    }
  } else {
    const text = await file.text();
    const delimiter = lower.endsWith('.tsv') ? '\t' : ',';
    applyRowsToSheet(state, parseCsvRows(text, delimiter));
  }
  state.dirty = true;
  renderCharts(state, elements);
  updateFormulaBar(state, elements);
  setStatus(elements, 'Imported');
}

function addChartFromSelection(state, elements) {
  const type = elements.chartType.val() || 'bar';
  const range = selectionRange(state.grid.sel);
  state.wb.addChart(state.activeSheet, {
    type,
    range,
    title: `${String(type).toUpperCase()} ${range}`
  });
  renderCharts(state, elements);
}

function applyAgentOps(state, elements, payload) {
  const ops = Array.isArray(payload.ops) ? payload.ops : [];
  ops.forEach((op) => {
    const si = sheetIndexFor(state.wb, state.activeSheet, op.sheet);
    if (op.op === 'setCells' && Array.isArray(op.cells)) {
      const updates = [];
      op.cells.forEach((cell) => {
        const parsed = parseCellName(String(cell.ref || ''));
        if (!parsed) return;
        const [r, c] = parsed;
        if (cell.formula !== undefined) {
          const formula = String(cell.formula || '');
          updates.push({ r, c, input: formula.startsWith('=') ? formula : `=${formula}` });
        } else if (cell.value !== undefined) {
          updates.push({ r, c, input: String(cell.value ?? '') });
        }
      });
      if (updates.length) state.wb.setCells(si, updates);
    } else if (op.op === 'addChart') {
      const type = ['bar', 'line', 'pie'].includes(op.type) ? op.type : 'bar';
      const range = String(op.range || selectionRange(state.grid.sel));
      state.wb.addChart(si, { type, range, title: String(op.title || `${type} ${range}`) });
    }
  });
  state.dirty = state.dirty || ops.length > 0;
  renderCharts(state, elements);
  updateFormulaBar(state, elements);
}

async function askAi(state, elements) {
  const instruction = String(elements.aiInput.val() || '').trim();
  if (!instruction) return;
  elements.aiRun.prop('disabled', true);
  elements.aiReply.text('Working...');
  try {
    const payload = await apiFetch('/api/sheets/agent', {
      method: 'POST',
      body: JSON.stringify({
        instruction,
        workbook_json: JSON.stringify(state.wb.model),
        active_sheet: state.wb.sheet(state.activeSheet).name,
        secret: getSecret()
      })
    });
    elements.aiReply.text(payload.reply || '');
    applyAgentOps(state, elements, payload);
    setStatus(elements, 'AI applied');
  } catch (error) {
    elements.aiReply.text(error.message || 'AI request failed');
    setStatus(elements, 'AI failed', true);
  } finally {
    elements.aiRun.prop('disabled', false);
  }
}

function bindEvents(state, elements) {
  elements.save.on('click', () => saveSheet(state, elements).catch((err) => setStatus(elements, err.message, true)));
  elements.newSheet.on('click', () => newSheet(state, elements));
  elements.openList.on('change', () => openSheet(state, elements, elements.openList.val()).catch((err) => setStatus(elements, err.message, true)));
  elements.exportCsv.on('click', () => downloadCsv(state, elements));
  elements.importFile.on('change', (event) => {
    importFile(state, elements, event.target.files && event.target.files[0]).catch((err) => setStatus(elements, err.message, true));
    event.target.value = '';
  });
  elements.formula.on('focus', () => {
    state.formulaFocused = true;
  });
  elements.formula.on('input', () => {
    if (state.grid.isEditing()) state.grid.setEditText(String(elements.formula.val() || ''));
  });
  elements.formula.on('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (state.grid.isEditing()) state.grid.commitEdit();
      else commitFormulaBar(state, elements);
      state.formulaFocused = false;
    } else if (event.key === 'Escape') {
      state.formulaFocused = false;
      updateFormulaBar(state, elements);
      state.grid.focus();
    }
  });
  elements.formula.on('blur', () => {
    if (state.formulaFocused) commitFormulaBar(state, elements);
    state.formulaFocused = false;
  });
  elements.addSheet.on('click', () => {
    const idx = state.wb.addSheet();
    state.activeSheet = idx;
    state.wb.model.active = idx;
    state.grid.setSheet(idx);
    renderTabs(state, elements);
    renderCharts(state, elements);
  });
  elements.renameSheet.on('click', () => {
    const current = state.wb.sheet(state.activeSheet).name;
    const name = window.prompt('Sheet name', current);
    if (name) {
      state.wb.renameSheet(state.activeSheet, name);
      renderTabs(state, elements);
    }
  });
  elements.deleteSheet.on('click', () => {
    if (state.wb.model.sheets.length <= 1) return;
    if (!window.confirm('Delete this sheet?')) return;
    state.wb.removeSheet(state.activeSheet);
    state.activeSheet = Math.max(0, Math.min(state.activeSheet, state.wb.model.sheets.length - 1));
    state.grid.setSheet(state.activeSheet);
    renderTabs(state, elements);
    renderCharts(state, elements);
  });
  elements.addChart.on('click', () => addChartFromSelection(state, elements));
  elements.aiRun.on('click', () => askAi(state, elements));
}

function collectElements(root) {
  return {
    root,
    title: root.find('[data-sheets-title]'),
    save: root.find('[data-sheets-save]'),
    newSheet: root.find('[data-sheets-new]'),
    openList: root.find('[data-sheets-open-list]'),
    exportCsv: root.find('[data-sheets-export-csv]'),
    importFile: root.find('[data-sheets-import]'),
    status: root.find('[data-sheets-status]'),
    cellRef: root.find('[data-sheets-cell-ref]'),
    formula: root.find('[data-sheets-formula]'),
    grid: root.find('[data-sheets-grid]'),
    tabs: root.find('[data-sheets-tabs]'),
    addSheet: root.find('[data-sheets-add-sheet]'),
    renameSheet: root.find('[data-sheets-rename-sheet]'),
    deleteSheet: root.find('[data-sheets-delete-sheet]'),
    chartType: root.find('[data-sheets-chart-type]'),
    addChart: root.find('[data-sheets-add-chart]'),
    charts: root.find('[data-sheets-charts]'),
    aiInput: root.find('[data-sheets-ai-input]'),
    aiRun: root.find('[data-sheets-ai-run]'),
    aiReply: root.find('[data-sheets-ai-reply]')
  };
}

function initSheetsPage() {
  const root = $('[data-sheets-page]');
  if (!root.length) return;
  const elements = collectElements(root);
  const state = {
    currentId: root.data('sheet-id') || null,
    wb: null,
    grid: null,
    activeSheet: 0,
    editing: false,
    formulaFocused: false,
    dirty: false
  };
  bindEvents(state, elements);
  setWorkbook(state, elements, new Workbook());
  loadSheets(elements)
    .then(() => {
      if (state.currentId) return openSheet(state, elements, state.currentId);
      return null;
    })
    .catch((error) => {
      setStatus(elements, error.message || 'Sign in to save', true);
    });
}

if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSheetsPage);
  } else {
    initSheetsPage();
  }
}

export { apiFetch, getSecret, initSheetsPage };
